import React, { useState } from 'react';
import { aiService } from '../../services/api';
import {
	SparklesIcon,
	ChartBarIcon,
	DocumentTextIcon,
	ExclamationCircleIcon,
	ArrowPathIcon
} from '@heroicons/react/24/outline';

const AIInsights = () => {
	const [question, setQuestion] = useState('');
	const [loading, setLoading] = useState(false);
	const [response, setResponse] = useState(null);
	const [error, setError] = useState(null);

	const sampleQuestions = [
		"What's our production performance this month?",
		"Which products have the highest demand?",
		"What are our inventory levels for critical parts?",
		"How many orders are pending fulfillment?",
		"What's our average order processing time?"
	];

	const handleAskQuestion = async (e) => {
		e.preventDefault();
		if (!question.trim()) return;

		setLoading(true);
		setError(null);

		try {
			const result = await aiService.askQuestion(question);
			setResponse(result);
		} catch (err) {
			setError(err.response?.data?.error || 'Failed to get AI insights. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	const handleSampleQuestion = (q) => {
		setQuestion(q);
	};

	const formatQueryResults = (results) => {
		if (!results || Object.keys(results).length === 0) return null;

		return Object.entries(results).map(([key, value]) => {
			if (value.error) {
				return (
					<div key={key} className="mb-4 p-3 bg-red-50 rounded-lg">
						<p className="text-red-700 text-sm">{value.error}</p>
					</div>
				);
			}

			if (!value.data || value.data.length === 0) {
				return (
					<div key={key} className="mb-4 p-3 bg-gray-50 rounded-lg">
						<p className="text-gray-600 text-sm italic">No data found for: {value.purpose}</p>
					</div>
				);
			}

			return (
				<div key={key} className="mb-4">
					<h4 className="text-sm font-medium text-gray-700 mb-2">{value.purpose}</h4>
					<div className="overflow-x-auto">
						<table className="min-w-full divide-y divide-gray-200 text-sm">
							<thead className="bg-gray-50">
							<tr>
								{Object.keys(value.data[0]).map((header) => (
									<th
										key={header}
										className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
									>
										{header.replace(/_/g, ' ')}
									</th>
								))}
							</tr>
							</thead>
							<tbody className="bg-white divide-y divide-gray-200">
							{value.data.slice(0, 5).map((row, idx) => (
								<tr key={idx}>
									{Object.values(row).map((cell, cellIdx) => (
										<td key={cellIdx} className="px-3 py-2 whitespace-nowrap text-gray-900">
											{cell !== null ? String(cell) : '-'}
										</td>
									))}
								</tr>
							))}
							</tbody>
						</table>
						{value.data.length > 5 && (
							<p className="text-xs text-gray-500 mt-2 px-3">
								Showing 5 of {value.data.length} results
							</p>
						)}
					</div>
				</div>
			);
		});
	};

	return (
		<div className="bg-white rounded-lg shadow overflow-hidden">
			<div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-primary-50 to-primary-100">
				<div className="flex items-center">
					<SparklesIcon className="h-6 w-6 text-primary-600 mr-2" />
					<h2 className="text-lg font-medium text-gray-900">Fit2Ride AI Insights</h2>
				</div>
				<p className="mt-1 text-sm text-gray-600">
					Ask questions about your business data and get instant insights
				</p>
			</div>

			<div className="p-6">
				<form onSubmit={handleAskQuestion} className="space-y-4">
					<div>
						<label htmlFor="ai-question" className="block text-sm font-medium text-gray-700 mb-2">
							Ask a question about your business
						</label>
						<div className="relative">
							<input
								type="text"
								id="ai-question"
								value={question}
								onChange={(e) => setQuestion(e.target.value)}
								placeholder="e.g., What's our best-selling product this month?"
								className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm pr-24 py-4 pl-4"
								disabled={loading}
							/>
							<button
								type="submit"
								disabled={loading || !question.trim()}
								className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
							>
								{loading ? (
									<>
										<ArrowPathIcon className="h-3 w-3 mr-1 animate-spin" />
										Thinking...
									</>
								) : (
									 'Ask'
								 )}
							</button>
						</div>
					</div>

					<div className="flex flex-wrap gap-2">
						<span className="text-xs text-gray-500">Try asking:</span>
						{sampleQuestions.map((q, idx) => (
							<button
								key={idx}
								type="button"
								onClick={() => handleSampleQuestion(q)}
								className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
							>
								{q}
							</button>
						))}
					</div>
				</form>

				{error && (
					<div className="mt-4 p-4 bg-red-50 rounded-lg">
						<div className="flex">
							<ExclamationCircleIcon className="h-5 w-5 text-red-400" />
							<div className="ml-3">
								<h3 className="text-sm font-medium text-red-800">Error</h3>
								<p className="mt-1 text-sm text-red-700">{error}</p>
							</div>
						</div>
					</div>
				)}

				{response && response.success && (
					<div className="mt-6 space-y-4">
						<div className="border-l-4 border-primary-500 pl-4">
							<h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
								<ChartBarIcon className="h-4 w-4 mr-2 text-primary-600" />
								Analysis
							</h3>
							{response.insights ? (
								<div className="prose prose-sm max-w-none text-gray-700">
									{response.insights.split('\n').map((paragraph, idx) => (
										paragraph.trim() && (
											<p key={idx} className="mb-2">
												{paragraph}
											</p>
										)
									))}
								</div>
							) : response.query_plan?.analysis && (
								<p className="text-sm text-gray-600">{response.query_plan.analysis}</p>
							)}
						</div>

						{response.results && Object.keys(response.results).length > 0 && (
							<div className="border-l-4 border-gray-300 pl-4">
								<h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
									<DocumentTextIcon className="h-4 w-4 mr-2 text-gray-600" />
									Data Results
								</h3>
								<div className="bg-gray-50 rounded-lg p-3">
									{formatQueryResults(response.results)}
								</div>
							</div>
						)}

						{response.metadata && (
							<div className="text-xs text-gray-500 text-right">
								Query executed at {new Date(response.metadata.timestamp).toLocaleTimeString()}
								{response.metadata.execution_time && ` • ${response.metadata.execution_time}`}
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	);
};

export default AIInsights;
